import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Search, Trash2, Truck, X, XCircle } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import { useVatLookup } from '../../hooks/useVatLookup';
import { newClientKey } from '../deposits/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import TouchKeyboard from '../ui/TouchKeyboard';
import CountryCodeInput, { countryLabel } from '../ui/CountryCodeInput';

/**
 * Δελτίο Αποστολής 9.3 από το μενού του ταμία (έγκριση χρήστη 2/11/2026· γραμμές με το χέρι από 3/11/2026): ό,τι φεύγει
 * από το κατάστημα — πρώτες ύλες που γυρίζουν στον προμηθευτή, συσκευή για service — με περιγραφή, ποσότητα και μονάδα,
 * όχι είδη του καταλόγου. Τα υπόλοιπα πεδία είναι της φόρμας του XENIOS. Κανένα παραστατικό πώλησης, καμία παραγγελία,
 * καμία πληρωμή — ο server (POST .../fiscal/delivery-note) εκδίδει και τυπώνει.
 *
 * Το κλειδί ζει όσο ισχύει η ίδια προσπάθεια: χωρίς απάντηση μένει, ώστε η επανάληψη να βρει το ίδιο δελτίο· με άρνηση
 * του server αλλάζει, ώστε η διορθωμένη φόρμα να βγάλει νέο.
 */

// Οι σκοποί που δέχεται ο server (services/deliveryNotes.js). Κανένας προεπιλεγμένος: το παραστατικό τον δηλώνει στην ΑΑΔΕ.
// Πώληση και εφοδιασμός πλοίων λείπουν επίτηδες: θέλουν μετά τιμολόγιο συνδεδεμένο με το δελτίο (Α.1047/2025).
const MOVE_PURPOSES = [5, 7, 8, 9, 11, 12, 14, 19];
const OTHER_PURPOSE = 19;

/** myDATA: 1 τεμάχια, 2 κιλά, 3 λίτρα */
const UNITS = [1, 2, 3] as const;
type Unit = typeof UNITS[number];

const MAX_DESCRIPTION = 300;

interface LineDraft {
  id: number;
  description: string;
  quantity: string;
  unit: Unit;
}

const emptyLine = (id: number): LineDraft => ({ id, description: '', quantity: '', unit: 1 });

/** «0,537» ή «0.537» → 0.537· ό,τι δεν είναι θετικός αριθμός → NaN. */
const parseQuantity = (value: string) => {
  const quantity = Number(value.replace(',', '.'));
  return quantity > 0 ? quantity : NaN;
};

/** Μόνο ψηφία και μία υποδιαστολή «,», έως τρία δεκαδικά (γραμμάρια, χιλιοστά του λίτρου). */
const filterQuantity = (value: string) => {
  const [whole, ...decimals] = value.replace(/\./g, ',').replace(/[^0-9,]/g, '').split(',');
  return decimals.length ? `${whole},${decimals.join('').slice(0, 3)}` : whole;
};

const isComplete = (line: LineDraft) => !!line.description.trim() && parseQuantity(line.quantity) > 0;
const isBlank = (line: LineDraft) => !line.description.trim() && !line.quantity;

type FormField = 'vatNumber' | 'name' | 'branchCode' | 'countryQuery' | 'street' | 'number' | 'city' | 'postalCode' | 'vehicleNumber' | 'otherPurpose';

const EMPTY_FORM: Record<FormField, string> = {
  vatNumber: '', name: '', branchCode: '', countryQuery: countryLabel('GR'),
  street: '', number: '', city: '', postalCode: '', vehicleNumber: '', otherPurpose: '',
};

const FIELD_FILTERS: Partial<Record<FormField, (value: string) => string>> = {
  vatNumber: value => value.replace(/[^0-9A-Za-z]/g, '').toUpperCase(),
  branchCode: value => value.replace(/\D/g, ''),
  postalCode: value => value.replace(/\D/g, ''),
};

const NUMERIC_FIELDS: Partial<Record<FormField, number>> = { branchCode: 3, postalCode: 5 };

/** Πού γράφει το πληκτρολόγιο οθόνης: πεδίο της φόρμας ή μέρος μιας γραμμής. */
type KeyboardTarget = { label: string } & ({ field: FormField } | { lineId: number; part: 'description' | 'quantity' });

export interface DeliveryNoteIssued {
  /** Εκδόθηκε, αλλά δεν έφτασε ακόμη στον πάροχο/ΑΑΔΕ: τυπώθηκε με την ένδειξη και ξαναστέλνεται αυτόματα. */
  pending: boolean;
}

interface Props {
  storeId: number;
  onClose: () => void;
  onIssued: (issued: DeliveryNoteIssued) => void;
}

const DeliveryNoteModal: React.FC<Props> = ({ storeId, onClose, onIssued }) => {
  const { t } = useTranslation(['takeaway', 'common']);
  const lookup = useVatLookup();
  const [form, setForm] = useState(EMPTY_FORM);
  const [countryCode, setCountryCode] = useState('GR');
  const [movePurpose, setMovePurpose] = useState('');
  const [dispatchNow, setDispatchNow] = useState(true);
  const [dispatchAt, setDispatchAt] = useState('');
  const [lines, setLines] = useState<LineDraft[]>(() => [emptyLine(0)]);
  const nextLineId = useRef(1);
  const [keyboard, setKeyboard] = useState<KeyboardTarget | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clientKey = useRef(newClientKey());

  const label = (key: string) => t(`takeaway:deliveryNote.${key}`);
  const setField = (field: FormField, value: string) => {
    const filter = FIELD_FILTERS[field];
    setForm(current => ({ ...current, [field]: filter ? filter(value) : value }));
  };

  const fieldProps = (field: FormField, fieldLabel: string) => ({
    'aria-label': fieldLabel,
    value: form[field],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setField(field, e.target.value),
    onFocus: () => setKeyboard({ field, label: fieldLabel }),
    className: 'mt-1 text-sm bg-surface-2 border-line-strong text-t1',
  });

  const updateLine = (id: number, patch: Partial<LineDraft>) =>
    setLines(current => current.map(line => (line.id === id ? { ...line, ...patch } : line)));

  const addLine = () => {
    const id = nextLineId.current++;
    setLines(current => [...current, emptyLine(id)]);
    setKeyboard({ lineId: id, part: 'description', label: `${label('description')} ${lines.length + 1}` });
  };

  // Η τελευταία γραμμή δεν φεύγει: αδειάζει, ώστε να υπάρχει πάντα πού να γράψεις.
  const removeLine = (id: number) => {
    setLines(current => (current.length > 1 ? current.filter(line => line.id !== id) : [emptyLine(nextLineId.current++)]));
    setKeyboard(current => (current && 'lineId' in current && current.lineId === id ? null : current));
  };

  const lineProps = (line: LineDraft, part: 'description' | 'quantity', partLabel: string) => ({
    'aria-label': partLabel,
    value: line[part],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => updateLine(line.id, {
      [part]: part === 'quantity' ? filterQuantity(e.target.value) : e.target.value.slice(0, MAX_DESCRIPTION),
    }),
    onFocus: () => setKeyboard({ lineId: line.id, part, label: partLabel }),
  });

  const lookupVat = async () => {
    setKeyboard(null);
    const found = await lookup.lookup(storeId, form.vatNumber);
    if (!found) return;
    setForm(current => ({
      ...current,
      vatNumber: found.vat_number || current.vatNumber,
      name: found.name || current.name,
      branchCode: found.branch_code ? String(found.branch_code) : current.branchCode,
      street: found.address?.street || current.street,
      number: found.address?.number || current.number,
      city: found.address?.city || current.city,
      postalCode: found.address?.postal_code || current.postalCode,
    }));
  };

  // Οι εντελώς κενές γραμμές αγνοούνται· μισογραμμένη γραμμή κρατά κλειστό το κουμπί.
  const filledLines = lines.filter(line => !isBlank(line));
  const purpose = Number(movePurpose);
  const ready = filledLines.length > 0 && filledLines.every(isComplete) && !!purpose
    && (purpose !== OTHER_PURPOSE || !!form.otherPurpose.trim())
    && !!form.name.trim() && !!form.street.trim() && !!form.city.trim() && !!form.postalCode.trim()
    && (dispatchNow || !!dispatchAt);

  const issue = async () => {
    if (!ready || issuing) return;
    setKeyboard(null);
    setError(null);
    setIssuing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${storeId}/fiscal/delivery-note`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
        },
        body: JSON.stringify({
          clientKey: clientKey.current,
          movePurpose: purpose,
          otherMovePurposeTitle: purpose === OTHER_PURPOSE ? form.otherPurpose.trim() : '',
          vehicleNumber: form.vehicleNumber.trim(),
          // «Τώρα»: τη στιγμή της έκδοσης την ορίζει ο server, όχι η ώρα που άνοιξε η φόρμα.
          dispatchedAt: dispatchNow ? null : new Date(dispatchAt).toISOString(),
          counterpart: {
            vat_number: form.vatNumber,
            name: form.name.trim(),
            branch_code: parseInt(form.branchCode, 10) || 0,
            country_code: countryCode,
          },
          deliveryAddress: {
            street: form.street.trim(),
            number: form.number.trim(),
            postal_code: form.postalCode.trim(),
            city: form.city.trim(),
          },
          lines: filledLines.map(line => ({
            description: line.description.trim(),
            quantity: parseQuantity(line.quantity),
            measurement_unit: line.unit,
          })),
        }),
      });
      const result = await response.json().catch(() => null);
      if (response.ok && (result?.success || result?.transmissionFailure)) {
        onIssued({ pending: !result.success });
        return;
      }
      if (response.ok && result?.inProgress) {
        setError(label('inProgress'));
        return;
      }
      clientKey.current = newClientKey();
      setError(result?.message || label('failed'));
    } catch {
      setError(label('noResponse'));
    } finally {
      setIssuing(false);
    }
  };

  const keyboardBinding = (target: KeyboardTarget) => {
    if ('field' in target) {
      return {
        mode: target.field in NUMERIC_FIELDS ? 'numeric' as const : 'text' as const,
        value: form[target.field],
        onChange: (value: string) => setField(target.field, value),
        maxLength: NUMERIC_FIELDS[target.field],
      };
    }
    const line = lines.find(l => l.id === target.lineId);
    if (!line) return null;
    return target.part === 'quantity'
      ? { mode: 'numeric' as const, decimal: true, value: line.quantity, onChange: (value: string) => updateLine(line.id, { quantity: filterQuantity(value) }) }
      : { mode: 'text' as const, value: line.description, maxLength: MAX_DESCRIPTION, onChange: (value: string) => updateLine(line.id, { description: value }) };
  };
  const binding = keyboard && keyboardBinding(keyboard);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" data-testid="delivery-note-modal">
      <div className="bg-surface-1 rounded-xl w-full max-w-2xl mx-4 shadow-2xl border border-line flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h2 className="text-xl font-bold text-t1 flex items-center gap-2">
            <Truck className="h-6 w-6 text-t2" />
            {label('title')}
          </h2>
          <button type="button" onClick={onClose} aria-label={t('common:close', { defaultValue: 'Κλείσιμο' })} className="text-t3 hover:text-t2">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto flex-1 px-6 pb-4">
          <div>
            <Label className="text-sm font-medium text-t2">{label('items')}</Label>
            <div className="mt-1 space-y-2" data-testid="delivery-note-lines">
              {lines.map((line, index) => (
                <div key={line.id} className="flex items-center gap-2">
                  <Input
                    {...lineProps(line, 'description', `${label('description')} ${index + 1}`)}
                    placeholder={label('descriptionPlaceholder')}
                    className="flex-1 min-w-0 text-sm bg-surface-2 border-line-strong text-t1"
                  />
                  <Input
                    {...lineProps(line, 'quantity', `${label('quantity')} ${index + 1}`)}
                    inputMode="decimal"
                    placeholder={label('quantity')}
                    className="w-24 shrink-0 text-sm text-right tabular-nums bg-surface-2 border-line-strong text-t1"
                  />
                  <div role="group" aria-label={`${label('unit')} ${index + 1}`} className="flex shrink-0 rounded-lg border border-line-strong overflow-hidden">
                    {UNITS.map(unit => (
                      <button
                        type="button"
                        key={unit}
                        aria-pressed={line.unit === unit}
                        onClick={() => updateLine(line.id, { unit })}
                        className={`h-10 px-3 text-sm font-semibold ${line.unit === unit ? 'bg-brand text-brand-fg' : 'bg-surface-2 text-t2 hover:bg-surface-3'}`}
                      >
                        {label(`units.${unit}`)}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLine(line.id)}
                    aria-label={`${label('removeLine')} ${index + 1}`}
                    className="h-10 w-10 shrink-0 rounded-lg text-t3 hover:text-red-600 hover:bg-surface-3 flex items-center justify-center"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addLine}
              className="mt-2 h-10 px-3 rounded-lg border border-dashed border-line-strong text-sm font-semibold text-t2 hover:bg-surface-3 hover:text-t1 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              {label('addLine')}
            </button>
          </div>

          <div>
            <Label htmlFor="delivery-note-purpose" className="text-sm font-medium text-t2">{label('purpose')}</Label>
            <select
              id="delivery-note-purpose"
              value={movePurpose}
              onChange={e => setMovePurpose(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 border border-line-strong rounded-lg text-sm bg-surface-2 text-t1"
            >
              <option value="">{label('purposePlaceholder')}</option>
              {MOVE_PURPOSES.map(code => (
                <option key={code} value={code}>{label(`purposes.${code}`)}</option>
              ))}
            </select>
            {purpose === OTHER_PURPOSE && (
              <Input {...fieldProps('otherPurpose', label('otherPurpose'))} placeholder={label('otherPurposePlaceholder')} />
            )}
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-t1">{label('recipient')}</h3>
            <div>
              <Label className="text-xs text-t3">{t('takeaway:vatNumber')}</Label>
              <div className="flex gap-2">
                <Input {...fieldProps('vatNumber', t('takeaway:vatNumber'))} />
                <Button type="button" onClick={lookupVat} disabled={lookup.isLoading || form.vatNumber.length < 9}
                  aria-label={label('lookup')} className="mt-1 bg-blue-600 hover:bg-blue-700">
                  {lookup.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-t3">{t('takeaway:companyName')}</Label>
              <Input {...fieldProps('name', t('takeaway:companyName'))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-t3">{t('takeaway:branchCode')}</Label>
                <Input {...fieldProps('branchCode', t('takeaway:branchCode'))} />
              </div>
              <div>
                <Label className="text-xs text-t3">{t('takeaway:countryCode')}</Label>
                <CountryCodeInput
                  value={countryCode}
                  query={form.countryQuery}
                  onQueryChange={value => setField('countryQuery', value)}
                  onSelect={code => {
                    setCountryCode(code);
                    setField('countryQuery', countryLabel(code));
                    setKeyboard(null);
                  }}
                  onFocus={() => setKeyboard({ field: 'countryQuery', label: t('takeaway:countryCode') })}
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-t1">{label('deliveryAddress')}</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs text-t3">{t('takeaway:street')}</Label>
                <Input {...fieldProps('street', t('takeaway:street'))} />
              </div>
              <div>
                <Label className="text-xs text-t3">{t('takeaway:number')}</Label>
                <Input {...fieldProps('number', t('takeaway:number'))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-t3">{t('takeaway:city')}</Label>
                <Input {...fieldProps('city', t('takeaway:city'))} />
              </div>
              <div>
                <Label className="text-xs text-t3">{t('takeaway:postalCode')}</Label>
                <Input {...fieldProps('postalCode', t('takeaway:postalCode'))} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-t3">{label('vehicle')}</Label>
              <Input {...fieldProps('vehicleNumber', label('vehicle'))} placeholder={label('vehiclePlaceholder')} />
            </div>
            <div>
              <Label className="text-xs text-t3">{label('dispatchTime')}</Label>
              <label className="mt-2 flex items-center gap-2 text-sm text-t2 cursor-pointer">
                <input type="checkbox" checked={dispatchNow} onChange={e => setDispatchNow(e.target.checked)} className="h-4 w-4" />
                {label('dispatchNow')}
              </label>
              {!dispatchNow && (
                <input
                  type="datetime-local"
                  aria-label={label('dispatchTime')}
                  value={dispatchAt}
                  onChange={e => setDispatchAt(e.target.value)}
                  className="mt-2 w-full px-2 py-2 border border-line-strong rounded-lg text-sm bg-surface-2 text-t1"
                />
              )}
            </div>
          </div>

        </div>

        {/* Έξω από την κύλιση: το μήνυμα πρέπει να φαίνεται δίπλα στο κουμπί που πατήθηκε, όπου κι αν έχει μείνει η φόρμα. */}
        <div className="px-6 pt-3 pb-5 border-t border-line space-y-3">
          {(error || lookup.error) && (
            <div role="alert" className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
              <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <span>{error || lookup.error}</span>
            </div>
          )}
          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="flex-1 h-14 rounded-xl border border-line-strong text-t2 text-base font-semibold hover:bg-surface-3">
              {t('takeaway:back')}
            </button>
            <button type="button" onClick={issue} disabled={!ready || issuing} data-testid="delivery-note-issue"
              className="flex-[2] h-14 rounded-xl bg-brand text-brand-fg text-base font-semibold flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed">
              {issuing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Truck className="h-5 w-5" />}
              {label('issue')}
            </button>
          </div>
        </div>
      </div>

      {keyboard && binding && (
        <TouchKeyboard
          {...binding}
          label={keyboard.label}
          onDone={() => setKeyboard(null)}
        />
      )}
    </div>
  );
};

export default DeliveryNoteModal;
